/**
 * Channels tab (§4.4.3).
 *
 * Top-level sub-tabs: Discord (active) + Telegram ("Coming soon").
 * Discord renders the shared ChannelsForm and wires its Apply button
 * to a PATCH agent call.
 */
import { useState } from 'react';
import type { AgentConfig, UpdateAgentPayload } from '@/api/agents';
import { useUpdateAgent } from '@/hooks/useAgent';
import { ChannelsForm } from '@/components/ChannelsForm';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ApiError } from '@/api/client';

interface ChannelsTabProps {
  agent: AgentConfig;
}

export default function Channels({ agent }: ChannelsTabProps) {
  const updateAgent = useUpdateAgent(agent.name);
  const [error, setError] = useState<string | null>(null);

  async function handleReview(patch: UpdateAgentPayload, version: string) {
    setError(null);
    try {
      await updateAgent.mutateAsync({ version, payload: patch });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to apply changes');
    }
  }

  return (
    <Tabs defaultValue="discord">
      <TabsList>
        <TabsTrigger value="discord">Discord</TabsTrigger>
        <TabsTrigger value="telegram" disabled className="gap-2">
          Telegram
          <Badge variant="secondary" className="text-[10px]">
            Coming soon
          </Badge>
        </TabsTrigger>
      </TabsList>
      <TabsContent value="discord" className="mt-4">
        <ChannelsForm agent={agent} onReview={handleReview} />
        {error && (
          <p className="mt-4 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </TabsContent>
      <TabsContent value="telegram" className="mt-4">
        <p className="text-sm text-muted-foreground">
          Telegram integration is planned for a future release.
        </p>
      </TabsContent>
    </Tabs>
  );
}
